import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import InfoTooltip from "../components/common/InfoTooltip";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import ReportPageHeader from "../components/reports/ReportPageHeader";
import { getLiveDashboard } from "../api/reports";
import { REPORT_HELP } from "../constants/reportHelpContent";
import { LIVE_DASHBOARD_TOOLTIPS } from "../constants/reportTooltips";
import { useAuth } from "../context/AuthContext";
import { getSalonTimezone } from "../utils/salonTimezone";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
  MODAL_TEXT,
} from "../ui/modalStyles";
import "./LiveDashboardReport.css";

const formatDateISO = (value, timeZone) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return "";
  if (timeZone) {
    try {
      return new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(value);
    } catch (error) {
      // Fall through to local fallback.
    }
  }
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (base, days) => {
  const next = new Date(base.getTime());
  next.setDate(next.getDate() + days);
  return next;
};

const formatMoney = (value) => {
  const number = Number(value ?? 0);
  if (!Number.isFinite(number)) return "$0.00";
  return `$${number.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatMoneyPerMinute = (value) => `${formatMoney(value)}/min`;

const formatPercent = (value) => {
  const number = Number(value ?? 0);
  if (!Number.isFinite(number)) return "0.0%";
  return `${number.toFixed(1)}%`;
};

const formatSignedPercent = (value) => {
  const number = Number(value ?? 0);
  if (!Number.isFinite(number)) return "0.0%";
  return `${number > 0 ? "+" : ""}${number.toFixed(1)}%`;
};

const getBarLabel = (point, index, total) => {
  if (!point) return "";
  if (total <= 8) return point.start_date?.slice(5) || point.label;
  const stride = Math.ceil(total / 6);
  return index % stride === 0 ? point.start_date?.slice(5) || point.label : "";
};

export default function LiveDashboardReport() {
  const navigate = useNavigate();
  const { currentSalon, user, token, loading: authLoading } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const helpGuide = REPORT_HELP.LIVE_DASHBOARD;

  const resolvedSalonId = useMemo(() => {
    const raw = currentSalon?.id ?? currentSalon?.salon_id ?? user?.salon_id ?? null;
    if (raw === null || raw === undefined || raw === "") return null;
    const asNumber = Number(raw);
    return Number.isFinite(asNumber) ? asNumber : null;
  }, [currentSalon, user?.salon_id]);

  const salonTimezone = useMemo(
    () => user?.salon_timezone || getSalonTimezone(currentSalon),
    [user?.salon_timezone, currentSalon]
  );
  const todayIso = useMemo(() => formatDateISO(new Date(), salonTimezone), [salonTimezone]);
  const defaultStartIso = useMemo(
    () => formatDateISO(addDays(new Date(), -6), salonTimezone),
    [salonTimezone]
  );

  const [startDate, setStartDate] = useState(defaultStartIso);
  const [endDate, setEndDate] = useState(todayIso);
  const [bucket, setBucket] = useState("day");
  const [rebookWindowDays, setRebookWindowDays] = useState(30);
  const topServicesLimit = 5;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [report, setReport] = useState(null);
  const hasAutoRunRef = useRef(false);

  useEffect(() => {
    setStartDate(defaultStartIso);
    setEndDate(todayIso);
    hasAutoRunRef.current = false;
  }, [defaultStartIso, todayIso]);

  useEffect(() => {
    if (authLoading) return;
    if (resolvedSalonId) return;
    setError("Select a salon to view reports.");
    const timeout = setTimeout(() => {
      navigate("/my-salons", { replace: true });
    }, 600);
    return () => clearTimeout(timeout);
  }, [authLoading, resolvedSalonId, navigate]);

  const dateRangeError = useMemo(() => {
    if (!startDate || !endDate) return "Start and end dates are required.";
    if (endDate < startDate) return "End date cannot be before start date.";
    return "";
  }, [startDate, endDate]);

  const payload = useMemo(
    () => ({
      start_date: startDate,
      end_date: endDate,
      rebook_window_days: Math.max(Number(rebookWindowDays) || 0, 0),
      top_services_limit: Math.max(Number(topServicesLimit) || 1, 1),
      bucket,
    }),
    [startDate, endDate, rebookWindowDays, topServicesLimit, bucket]
  );

  const fetchReport = useCallback(
    async (payloadOverride = null) => {
      if (authLoading) return;
      if (!resolvedSalonId) return;
      if (dateRangeError) {
        setError(dateRangeError);
        return;
      }

      setLoading(true);
      setError("");
      try {
        const data = await getLiveDashboard(payloadOverride || payload, token);
        setReport(data || null);
      } catch (err) {
        setReport(null);
        setError(err?.response?.data?.detail || "Unable to load live dashboard.");
      } finally {
        setLoading(false);
      }
    },
    [authLoading, resolvedSalonId, dateRangeError, payload, token]
  );

  useEffect(() => {
    if (authLoading || !resolvedSalonId || hasAutoRunRef.current) return;
    hasAutoRunRef.current = true;
    fetchReport(payload);
  }, [authLoading, resolvedSalonId, fetchReport, payload]);

  const renderFieldLabel = (label, tooltipKey) => (
    <div className="flex items-center gap-1.5">
      <label className={LABEL_TEXT}>{label}</label>
      {tooltipKey ? (
        <InfoTooltip text={LIVE_DASHBOARD_TOOLTIPS[tooltipKey]} label={label} />
      ) : null}
    </div>
  );

  const revenueSeries = Array.isArray(report?.revenue_series) ? report.revenue_series : [];
  const maxRevenue = Math.max(
    1,
    ...revenueSeries.map((point) => Number(point?.value || 0))
  );
  const noTicketData = !loading && !error && report && Number(report.ticket_count || 0) === 0;

  return (
    <div className="liveDashboardPageShell min-h-screen bg-slate-100">
      <div className="liveDashboardPageWide live-dashboard-page-compact">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <ReportPageHeader title="Live Dashboard" />

          <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
            <div
              id="live-dashboard-overview"
              className={getSectionClassName("live-dashboard-overview")}
            >
              <ReportHelpCard
                title={helpGuide.title}
                subtitle={helpGuide.subtitle}
                sections={helpGuide.sections}
                troubleshooting={helpGuide.troubleshooting}
                storageKey="report_help_live_dashboard"
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>

            {error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                {error}
              </div>
            ) : null}

            <div
              id="live-dashboard-filters"
              className={`rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-4 ${getSectionClassName(
                "live-dashboard-filters"
              )}`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <div>
                  {renderFieldLabel("Start Date", "start_date")}
                  <input
                    type="date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
                <div>
                  {renderFieldLabel("End Date", "end_date")}
                  <input
                    type="date"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
                <div>
                  {renderFieldLabel("Bucket", "bucket")}
                  <select
                    value={bucket}
                    onChange={(event) => setBucket(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  >
                    <option value="day">Day</option>
                    <option value="week">Week</option>
                  </select>
                </div>
                <div>
                  {renderFieldLabel("Rebook Window (days)", "rebooking_rate")}
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={rebookWindowDays}
                    onChange={(event) => setRebookWindowDays(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
              </div>

              {dateRangeError ? (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                  {dateRangeError}
                </div>
              ) : null}

              <div className="flex flex-wrap items-center gap-3">
                <div className="text-xs text-slate-500">
                  Salon timezone: {report?.tz_name || salonTimezone}
                </div>
                <div className="ml-auto flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => fetchReport(payload)}
                    className={`${BTN_PRIMARY} min-w-[140px]`}
                    disabled={loading}
                  >
                    {loading ? "Loading..." : "Run Report"}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/manager/reports")}
                    className={`${BTN_OUTLINE} min-w-[140px]`}
                    disabled={loading}
                  >
                    Back to Reports
                  </button>
                </div>
              </div>
            </div>

            {noTicketData ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-6 text-slate-600">
                No tickets found for this date range.
              </div>
            ) : null}

            <div
              id="live-dashboard-kpis"
              className={`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 ${getSectionClassName(
                "live-dashboard-kpis"
              )}`}
            >
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-500">
                  <span>Sales</span>
                  <InfoTooltip text={LIVE_DASHBOARD_TOOLTIPS.sales} label="Sales" />
                </div>
                <div className="mt-2 text-2xl font-semibold text-slate-900">
                  {formatMoney(report?.sales)}
                </div>
                <div className="mt-2 text-sm text-slate-600 flex items-center gap-1.5">
                  <span>{formatSignedPercent(report?.sales_change_pct_vs_prev)} vs previous period</span>
                  <InfoTooltip text={LIVE_DASHBOARD_TOOLTIPS.sales_change} label="Previous period change" />
                </div>
                <div className="mt-1 text-sm text-slate-500 flex items-center gap-1.5">
                  <span>Avg ticket {formatMoney(report?.avg_ticket)}</span>
                  <InfoTooltip text={LIVE_DASHBOARD_TOOLTIPS.avg_ticket} label="Avg ticket" />
                </div>
                <div className="mt-1 text-sm text-slate-500">
                  {Number(report?.ticket_count || 0)} tickets
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-500">
                  <span>Rebooking</span>
                  <InfoTooltip text={LIVE_DASHBOARD_TOOLTIPS.rebooking_rate} label="Rebooking rate" />
                </div>
                <div className="mt-2 text-2xl font-semibold text-slate-900">
                  {formatPercent(report?.rebooking_rate_pct)}
                </div>
                <div className="mt-2 text-sm text-slate-500">30-day return rate</div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-500">
                  <span>Revenue / Booked Min</span>
                  <InfoTooltip
                    text={LIVE_DASHBOARD_TOOLTIPS.revenue_per_booked_minute}
                    label="Revenue per booked minute"
                  />
                </div>
                <div className="mt-2 text-2xl font-semibold text-slate-900">
                  {formatMoneyPerMinute(report?.revenue_per_booked_minute)}
                </div>
                <div className="mt-2 text-sm text-slate-500">
                  Net sales divided by booked appointment minutes
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-500">
                  <span>Staff Utilization</span>
                  <InfoTooltip text={LIVE_DASHBOARD_TOOLTIPS.utilization} label="Staff utilization" />
                </div>
                <div className="mt-2 text-2xl font-semibold text-slate-900">
                  {formatPercent(report?.utilization_pct)}
                </div>
                <div className="mt-2 text-sm text-slate-500">Booked vs available hours</div>
              </div>
            </div>

            <div
              id="live-dashboard-details"
              className={`space-y-6 ${getSectionClassName("live-dashboard-details")}`}
            >
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
                <div className="flex items-center gap-1.5 text-base md:text-lg font-semibold text-slate-900">
                  <span>Revenue trend</span>
                  <InfoTooltip text={LIVE_DASHBOARD_TOOLTIPS.revenue_trend} label="Revenue trend" />
                </div>
                <div className="rounded-2xl border border-rose-100 bg-rose-50/60 p-4">
                  <div className="h-52 flex items-end gap-2">
                    {revenueSeries.length ? (
                      revenueSeries.map((point, index) => {
                        const value = Number(point?.value || 0);
                        const height = Math.max(4, Math.round((value / maxRevenue) * 100));
                        return (
                          <div
                            key={`${point.start_date}-${point.end_date}-${index}`}
                            className="flex-1 min-w-0 flex flex-col items-center gap-2"
                          >
                            <div className="w-full flex items-end h-40">
                              <div
                                className="w-full rounded-t-xl bg-rose-400 transition-all"
                                style={{ height: `${height}%` }}
                                title={`${point.label}: ${formatMoney(value)}`}
                              />
                            </div>
                            <div className="text-[11px] leading-tight text-slate-500 text-center min-h-[1rem]">
                              {getBarLabel(point, index, revenueSeries.length)}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="w-full h-full grid place-items-center text-slate-500 text-sm">
                        {loading ? "Loading trend..." : "No trend data in this range."}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-1.5 text-base md:text-lg font-semibold text-slate-900">
                  <span>Top Services</span>
                  <InfoTooltip text={LIVE_DASHBOARD_TOOLTIPS.top_services} label="Top services" />
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm md:text-base">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold">Service</th>
                        <th className="px-4 py-3 text-right font-semibold">Tickets</th>
                        <th className="px-4 py-3 text-right font-semibold">Avg ticket</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(report?.top_services || []).slice(0, topServicesLimit).map((row, index) => (
                        <tr
                          key={`${row.service_name}-${index}`}
                          className="border-t border-slate-100"
                        >
                          <td className="px-4 py-3 text-slate-800">{row.service_name}</td>
                          <td className="px-4 py-3 text-right text-slate-700">{row.ticket_count}</td>
                          <td className="px-4 py-3 text-right text-slate-700">
                            {formatMoney(row.avg_service_price)}
                          </td>
                        </tr>
                      ))}
                      {!loading && !(report?.top_services || []).length ? (
                        <tr>
                          <td colSpan={3} className="px-4 py-8 text-center text-slate-500">
                            No service rows for this date range.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div
              id="live-dashboard-export"
              className={`rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600 ${getSectionClassName(
                "live-dashboard-export"
              )}`}
            >
              Export is not available on this report.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
